import {
  Badge,
  Stack,
  Button,
  Drawer,
  Divider,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { FC, useMemo, useState, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRange } from './fields/date-range';
import Iconify from '@/components/media/iconify';
import Scrollbar from '@/components/layout/scrollbar';
import { NumericRange } from './fields/numeric-range';
import { FilterField } from './types';
import { AutocompleteSelect } from './fields/autocomplete-select';
import useIsDirty from './hooks/use-dirty.hook';
import { useResetFilters } from './hooks';
import { CheckboxFilter } from './fields/checkbox';
import { MonthRange } from './fields/month-range';
import { SearchText } from './fields/search-text';
import { AdvancedTextFilter } from './fields/advanced-text-filter';
import { AdvancedDateFilter } from './fields/advanced-date-filter';

export * from './types';
export * from './filter-hub-result';

type TFilter = {
  [key: string]: any;
};

type Props = {
  filters: TFilter;
  onChange: (value: SetStateAction<any>) => void;
  fields: FilterField[];
  canReset?: boolean;
  forceShowReset?: boolean; // Force show reset button regardless of isDirty
  onResetFilters?: VoidFunction; // Optional custom reset function
};

const FiltersHub: FC<Props> = ({
  filters,
  onChange,
  fields,
  canReset = true,
  forceShowReset = false,
  onResetFilters,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleDrawerOpen = () => setIsOpen(true);
  const handleDrawerClose = () => setIsOpen(false);

  const fieldsNames = useMemo(() => fields.map((field) => field.name), [fields]);
  const isDirty = useIsDirty(fieldsNames, filters);
  const defaultHandleReset = useResetFilters({ filters, fields, onChange });

  // Use custom reset function if provided, otherwise use default
  const handleReset = onResetFilters || defaultHandleReset;

  // Show reset button if canReset is true AND (isDirty is true OR forceShowReset is true)
  const showResetButton = canReset && (isDirty || forceShowReset);

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={
          <Badge color="error" variant="dot" invisible={!showResetButton}>
            <Iconify icon="ic:round-filter-list" />
          </Badge>
        }
        onClick={handleDrawerOpen}
      >
        {t('Filters')}
      </Button>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleDrawerClose}
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 370 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 2, pr: 1, pl: 2.5 }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('Filters')}
          </Typography>

          <Tooltip title={t('Reset')}>
            <IconButton onClick={handleReset}>
              <Badge color="error" variant="dot" invisible={!showResetButton}>
                <Iconify icon="solar:restart-bold" />
              </Badge>
            </IconButton>
          </Tooltip>

          <IconButton onClick={handleDrawerClose}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>

        <Divider />

        <Scrollbar sx={{ px: 2.5, py: 3 }}>
          <Grid container spacing={3}>
            {fields.map((field) => (
              <Grid key={field.name} xs={12}>
                {field.type === 'numeric-range' && (
                  <NumericRange {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'date-range' && (
                  <DateRange {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'autocomplete' && (
                  <AutocompleteSelect {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'month-range' && (
                  <MonthRange {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'checkbox' && (
                  <CheckboxFilter {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'search-text' && (
                  <SearchText {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'advanced-text' && (
                  <AdvancedTextFilter {...field} filters={filters} onChange={onChange} />
                )}

                {field.type === 'advanced-date' && (
                  <AdvancedDateFilter {...field} filters={filters} onChange={onChange} />
                )}
              </Grid>
            ))}
          </Grid>
        </Scrollbar>
      </Drawer>
    </>
  );
};

export default FiltersHub;
